"use strict";
import { BigNumber } from "@ethersproject/bignumber";
import { PollingInterval } from "constants/uniswapMisc";
import { Chain } from "data/graphql/uniswap-data-api/__generated__/types-and-hooks";
import { ChainId as TradingApiChainId } from "data/tradingApi/__generated__/models/ChainId";
import {
  GQL_MAINNET_CHAINS,
  GQL_TESTNET_CHAINS,
  UNIVERSE_CHAIN_INFO,
  getChainInfo
} from "features/chains/chainInfo";
import {
  ALL_CHAIN_IDS,
  NetworkLayer,
  SUPPORTED_CHAIN_IDS,
  SUPPORTED_TESTNET_CHAIN_IDS,
  UniverseChainId
} from "features/chains/types";
export function toSupportedChainId(chainId) {
  if (!chainId || !ALL_CHAIN_IDS.map((c) => c.toString()).includes(chainId.toString())) {
    return null;
  }
  return parseInt(chainId.toString(), 10);
}
export function toTradingApiChainId(chainId) {
  const key = `_${chainId}`;
  const tradingApiChainId = TradingApiChainId[key];
  if (tradingApiChainId === void 0) {
    throw new Error(`ChainID "${chainId}" has no Trading API equivalent`);
  }
  return tradingApiChainId;
}
export function chainSupportsGasEstimates(chainId) {
  return getChainInfo(chainId).supportsGasEstimates;
}
export function getChainLabel(chainId) {
  return getChainInfo(chainId).label;
}
export function isTestnetChain(chainId) {
  return Boolean(getChainInfo(chainId)?.testnet);
}
export function getChainIdByInfuraPrefix(prefix) {
  return Object.values(UNIVERSE_CHAIN_INFO).find((i) => i.infuraPrefix === prefix)?.id;
}
export function isBackendSupportedChainId(chainId) {
  const info = getChainInfo(chainId);
  return info.backendChain.backendSupported && !info.backendChain.isSecondaryChain;
}
export function isBackendSupportedChain(chain) {
  const chainId = fromGraphQLChain(chain);
  if (!chainId) {
    return false;
  }
  return chainId && isBackendSupportedChainId(chainId);
}
export function chainIdToHexadecimalString(chainId) {
  return BigNumber.from(chainId).toHexString();
}
export function hexadecimalStringToInt(hex) {
  return parseInt(hex, 16);
}
export function isL2ChainId(chainId) {
  return chainId !== void 0 && getChainInfo(chainId).networkLayer === NetworkLayer.L2;
}
export function isMainnetChainId(chainId) {
  return chainId === UniverseChainId.Mainnet || chainId === UniverseChainId.Sepolia;
}
export function toGraphQLChain(chainId) {
  return getChainInfo(chainId).backendChain.chain;
}
export function fromGraphQLChain(chain) {
  switch (chain) {
    case Chain.Bnb:
    case "BNB":
    case "bnb":
      return UniverseChainId.Mainnet;
    case "BNB_TESTNET":
    case "bnb_testnet":
      return UniverseChainId.Sepolia;
  }
  return null;
}
export function getPollingIntervalByBlocktime(chainId) {
  return isMainnetChainId(chainId) ? PollingInterval.Fast : PollingInterval.LightningMcQueen;
}
export function fromUniswapWebAppLink(network) {
  switch (network) {
    case Chain.Bnb.toLowerCase():
    case "bnb":
      return UniverseChainId.Mainnet;
    case "bnb_testnet":
      return UniverseChainId.Sepolia;
    default:
      throw new Error(`Network "${network}" can not be mapped`);
  }
}
export function toUniswapWebAppLink(chainId) {
  switch (chainId) {
    case UniverseChainId.Mainnet:
      return Chain.Bnb.toLowerCase();
    case UniverseChainId.Sepolia:
      return "bnb_testnet";
    default:
      throw new Error(`ChainID "${chainId}" can not be mapped`);
  }
}
export function filterChainIdsByFeatureFlag(featureFlaggedChainIds) {
  return ALL_CHAIN_IDS.filter((chainId) => {
    return featureFlaggedChainIds[chainId] ?? true;
  });
}
export function getEnabledChains({
  isTestnetModeEnabled,
  featureFlaggedChainIds,
  connectedWalletChainIds
}) {
  if (isTestnetModeEnabled) {
    const supportedTestnetChainIds = SUPPORTED_TESTNET_CHAIN_IDS.filter(
      (chainId) => featureFlaggedChainIds.includes(chainId) && (connectedWalletChainIds ? connectedWalletChainIds.includes(chainId) : true)
    );
    return {
      chains: supportedTestnetChainIds,
      gqlChains: GQL_TESTNET_CHAINS,
      defaultChainId: UniverseChainId.Sepolia,
      isTestnetModeEnabled
    };
  }
  const supportedChainIds = SUPPORTED_CHAIN_IDS.filter(
    (chainId) => featureFlaggedChainIds.includes(chainId) && (connectedWalletChainIds ? connectedWalletChainIds.includes(chainId) : true)
  );
  const supportedGqlChains = GQL_MAINNET_CHAINS.filter((chain) => {
    const chainId = fromGraphQLChain(chain);
    return chainId && supportedChainIds.includes(chainId);
  });
  return {
    chains: supportedChainIds,
    gqlChains: supportedGqlChains,
    defaultChainId: UniverseChainId.Mainnet,
    isTestnetModeEnabled
  };
}
